/**
 * Single source of truth: map nav/UI permission keys to Spatie permissions
 * returned by the admin auth API. Mirrors backend AdminPermissionCatalog::legacyAliases().
 */
export const ADMIN_PERMISSION_ALIASES: Record<string, string> = {
  'analytics.view': 'view_analytics',
  'moderation.review': 'manage_events',
  'moderation.manage': 'manage_events',
  'finance.read': 'manage_payments',
  'finance.manage': 'manage_payments',
  'content.manage': 'manage_pages',
  'security.read': 'manage_settings',
  'security.manage': 'manage_settings',
  'system.manage': 'manage_settings',
  'support.read': 'manage_events',
  'support.manage': 'manage_events',
  'view_kyc': 'view_kyc',
  'review_kyc': 'review_kyc',
  'approve_kyc': 'approve_kyc',
  'reject_kyc': 'reject_kyc',
  'view_settlement_accounts': 'view_settlement_accounts',
  'review_settlement_accounts': 'review_settlement_accounts',
  'verify_settlement_accounts': 'verify_settlement_accounts',
  'reject_settlement_accounts': 'reject_settlement_accounts',
  'view_organizer_requests': 'view_organizer_requests',
  'review_organizer_requests': 'review_organizer_requests',
  'approve_organizer_requests': 'approve_organizer_requests',
  'reject_organizer_requests': 'reject_organizer_requests',
  'suspend_organizers': 'suspend_organizers',
  'view_settlements': 'view_settlements',
  'review_settlements': 'review_settlements',
  'approve_settlements': 'approve_settlements',
  'process_settlements': 'process_settlements',
  'freeze_settlements': 'freeze_settlements',
  'view_refunds': 'view_refunds',
  'review_refunds': 'review_refunds',
  'approve_refunds': 'approve_refunds',
  'process_refunds': 'process_refunds',
  'force_refunds': 'force_refunds',
  'view_finance_dashboard': 'view_finance_dashboard',
  'view_legal_consents': 'view_legal_consents',
  'davs.view': 'davs.view',
  'view_financial_reports': 'view_financial_reports',
  'view_transaction_ledger': 'view_transaction_ledger',
  'view_financial_audits': 'view_financial_audits',
  'view_event_governance': 'view_event_governance',
  'review_events': 'review_events',
  'hold_events': 'hold_events',
  'suspend_events': 'suspend_events',
  'reactivate_events': 'reactivate_events',
  'force_event_refunds': 'force_event_refunds',
  'freeze_event_settlements': 'freeze_event_settlements',
};

export function resolveAdminPermissionKey(key: string): string {
  const normalized = key.trim().toLowerCase();
  return ADMIN_PERMISSION_ALIASES[normalized] ?? normalized;
}

export function resolveAdminPermissionRequirement(
  requirement: string | string[] | undefined,
): string | string[] | undefined {
  if (!requirement) return requirement;
  if (Array.isArray(requirement)) {
    return requirement.map((k) => resolveAdminPermissionKey(k));
  }
  return resolveAdminPermissionKey(requirement);
}

/** Tabs restricted to Master Admin + Super Admin (hidden entirely for others). */
export const SUPER_PLATFORM_TABS = new Set<string>([
  'settings',
  'smtp',
  'storage',
  'payment-gateways',
  'feature-flags',
  'platform-readiness',
  'platform-health',
  'platform-performance',
  'security-center',
  'marketplace',
  'release-ops',
  'session-monitoring',
  'ip-restrictions',
  'audit-integrity',
  'security-logs',
  'operations-dashboard',
  'queues',
  'scheduler',
  'logs',
  'cache',
  'backups',
  'search-index',
  'notification-policies',
  'communication-senders',
  'workflow-analytics',
  'launch-readiness',
  'audit-coverage',
  'navigation-health',
  'workflow-certification',
  'crud-certification',
]);

/** Dedicated Next.js admin routes that require super platform access. */
export const SUPER_PLATFORM_DEDICATED_PATHS = [
  '/dashboard/admin/platform-performance',
  '/dashboard/admin/security-center',
  '/dashboard/admin/marketplace',
  '/dashboard/admin/release-ops',
  '/dashboard/admin/platform-health',
  '/dashboard/admin/queues',
  '/dashboard/admin/scheduler',
  '/dashboard/admin/logs',
  '/dashboard/admin/cache',
  '/dashboard/admin/backups',
  '/dashboard/admin/search-index',
  '/dashboard/admin/notification-policies',
  '/dashboard/admin/communication-senders',
] as const;

export function isSuperPlatformTab(tab: string): boolean {
  return SUPER_PLATFORM_TABS.has(tab);
}
